import { useState, useEffect } from 'react';
import { fetchCampaigns, cancelCampaign, deleteCampaign, cloneCampaign } from '../../api/relances.api';

const STATUS_BADGES = {
  DRAFT:     { bg: 'bg-slate-100',   text: 'text-slate-700',   label: 'Brouillon' },
  SCHEDULED: { bg: 'bg-sky-100',     text: 'text-sky-800',     label: 'Planifiée' },
  RUNNING:   { bg: 'bg-violet-100',  text: 'text-violet-800',  label: 'En cours' },
  DONE:      { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Terminée' },
  CANCELLED: { bg: 'bg-red-100',     text: 'text-red-800',     label: 'Annulée' },
};

const RELANCE_TYPES = {
  PAYMENT_REMINDER:  { label: 'Rappel paiement',  icon: '💰' },
  OVERDUE_INVOICE:   { label: 'Facture impayée',  icon: '📄' },
  PRESTATION_REMINDER: { label: 'Rappel prestation', icon: '🔧' },
  ORDER_FOLLOWUP:    { label: 'Suivi commande',   icon: '📦' },
  CUSTOM:            { label: 'Personnalisé',     icon: '✏️' },
};

const FILTER_STATUS_OPTIONS = [
  { value: 'DRAFT',     label: 'Brouillon' },
  { value: 'READY',     label: 'Planifiée' },
  { value: 'SENDING',   label: 'En cours' },
  { value: 'COMPLETED', label: 'Terminée' },
  { value: 'FAILED',    label: 'Échouée' },
  { value: 'CANCELLED', label: 'Annulée' },
];

function mapUiStatus(campaign) {
  if (campaign.status === 'DRAFT')                                   return 'DRAFT';
  if (campaign.status === 'READY')     return campaign.scheduled_at ? 'SCHEDULED' : 'DRAFT';
  if (campaign.status === 'SENDING')                                 return 'RUNNING';
  if (['COMPLETED', 'FAILED'].includes(campaign.status))            return 'DONE';
  if (campaign.status === 'CANCELLED')                               return 'CANCELLED';
  return 'DRAFT';
}

export default function CampaignList({ onViewCampaign, onNewCampaign }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    relanceType: '',
    search: ''
  });
  const [pagination, setPagination] = useState({ page: 1, total: 0, limit: 20 });

  // Réinitialiser à la page 1 quand les filtres changent
  useEffect(() => {
    setPagination(p => ({ ...p, page: 1 }));
  }, [filters.status, filters.relanceType, filters.search]);

  useEffect(() => {
    loadCampaigns();
  }, [filters, pagination.page]);

  const loadCampaigns = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchCampaigns({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      });
      setCampaigns(result.campaigns || []);
      setPagination(p => ({ ...p, total: result.total || 0 }));
    } catch (err) {
      setError('Erreur lors du chargement des campagnes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id) => {
    if (!confirm('Annuler cette campagne ?')) return;
    try {
      await cancelCampaign(id);
      loadCampaigns();
    } catch (err) {
      alert('Erreur lors de l\'annulation');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer définitivement cette campagne ?')) return;
    try {
      await deleteCampaign(id);
      loadCampaigns();
    } catch (err) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleClone = async (id, e) => {
    e.stopPropagation();
    try {
      await cloneCampaign(id);
      loadCampaigns();
    } catch (err) {
      alert(err?.message || 'Erreur lors de la duplication');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const stats = campaigns.reduce((acc, c) => {
    acc.total += 1;
    if (['READY', 'SENDING'].includes(c.status)) acc.active += 1;
    if (c.scheduled_at) acc.scheduled += 1;
    if (c.sent_count) acc.sent += Number(c.sent_count || 0);
    return acc;
  }, { total: 0, active: 0, scheduled: 0, sent: 0 });

  const upcoming = campaigns
    .filter(c => c.scheduled_at)
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
    .slice(0, 5);

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div>
      {/* Filters + Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              placeholder="Rechercher une campagne..."
              value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-slate-900 text-sm w-full md:w-[240px]"
            />
            <select
              value={filters.status}
              onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-slate-900 text-sm"
            >
              <option value="">Tous les statuts</option>
              {FILTER_STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select
              value={filters.relanceType}
              onChange={e => setFilters(f => ({ ...f, relanceType: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-slate-900 text-sm"
            >
              <option value="">Tous les types</option>
              {Object.entries(RELANCE_TYPES).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
            <button
              onClick={onNewCampaign}
              className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-950 transition-colors flex items-center gap-2 text-sm font-semibold"
            >
              ➕ Nouvelle campagne
            </button>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs text-slate-500">Synthèse</div>
          <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-slate-500">Total</div>
              <div className="font-semibold text-slate-900">{stats.total}</div>
            </div>
            <div>
              <div className="text-slate-500">Actives</div>
              <div className="font-semibold text-slate-800">{stats.active}</div>
            </div>
            <div>
              <div className="text-slate-500">Planifiées</div>
              <div className="font-semibold text-emerald-700">{stats.scheduled}</div>
            </div>
            <div>
              <div className="text-slate-500">Envoyés</div>
              <div className="font-semibold text-slate-900">{stats.sent}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">
          Chargement...
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-slate-500 mb-4">Aucune campagne trouvée</p>
          <button
            onClick={onNewCampaign}
            className="text-slate-700 hover:underline"
          >
            Créer votre première campagne
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Campaigns table */}
            <div className="lg:col-span-3 bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs text-slate-500 font-semibold">
                Campagnes récentes
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Campagne</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Type</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Statut</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500">Destinataires</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500">Envoyés</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500">Taux</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Date</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map(campaign => {
                      const uiStatus = mapUiStatus(campaign);
                      const statusBadge = STATUS_BADGES[uiStatus] || STATUS_BADGES.DRAFT;
                      const typeInfo = RELANCE_TYPES[campaign.relance_type] || RELANCE_TYPES.CUSTOM;

                      return (
                        <tr
                          key={campaign.id}
                          className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                          onClick={() => onViewCampaign(campaign.id)}
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-900">{campaign.name}</span>
                              {campaign.is_dry_run && (
                                <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-700">
                                  TEST
                                </span>
                              )}
                            </div>
                            {campaign.scheduled_at && (
                              <div className="text-xs text-slate-500 mt-1">📅 {formatDate(campaign.scheduled_at)}</div>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <span className="inline-flex items-center gap-1 text-sm text-slate-600">
                              <span>{typeInfo.icon}</span>
                              {typeInfo.label}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                              {statusBadge.label}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="text-sm text-slate-900">{campaign.total_recipients || 0}</span>
                            <div className="text-xs text-slate-500">
                              📧 {campaign.email_count || 0} / 📱 {campaign.whatsapp_count || 0}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="text-sm text-emerald-600">{campaign.sent_count || 0}</span>
                            {campaign.failed_count > 0 && (
                              <span className="text-xs text-red-500 ml-1">({campaign.failed_count} échecs)</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {(['COMPLETED', 'SENDING'].includes(campaign.status) && campaign.total_recipients > 0) ? (() => {
                              const rate = Math.round((campaign.sent_count || 0) / campaign.total_recipients * 100);
                              const color = rate >= 80 ? 'bg-emerald-100 text-emerald-700' : rate >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700';
                              return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}>{rate}%</span>;
                            })() : <span className="text-slate-400 text-sm">—</span>}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-500">
                            {formatDate(campaign.created_at)}
                          </td>
                          <td className="py-3 px-4 text-right" onClick={e => e.stopPropagation()}>
                            <div className="flex justify-end gap-2">
                              {campaign.status !== 'SENDING' && (
                                <button
                                  onClick={(e) => handleClone(campaign.id, e)}
                                  className="text-slate-500 hover:text-slate-700 text-sm"
                                  title="Dupliquer"
                                >
                                  ⧉
                                </button>
                              )}
                              {['DRAFT', 'READY', 'SENDING'].includes(campaign.status) && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleCancel(campaign.id); }}
                                  className="text-orange-600 hover:text-orange-700 text-sm"
                                  title="Annuler"
                                >
                                  ✕
                                </button>
                              )}
                              {['DRAFT', 'CANCELLED'].includes(campaign.status) && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDelete(campaign.id); }}
                                  className="text-red-600 hover:text-red-700 text-sm"
                                  title="Supprimer"
                                >
                                  🗑
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Calendar panel */}
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-sm font-semibold text-slate-900 mb-2">📅 Prochains envois</div>
              {upcoming.length === 0 ? (
                <div className="text-sm text-slate-500">
                  Aucun envoi planifié.
                </div>
              ) : (
                <div className="space-y-3">
                  {upcoming.map(c => (
                    <div key={c.id} className="rounded-lg border border-slate-200 p-3">
                      <div className="text-sm font-semibold text-slate-900 truncate">{c.name}</div>
                      <div className="text-xs text-slate-500 mt-1">{formatDate(c.scheduled_at)}</div>
                      <div className="text-xs text-slate-500 mt-1">📧 {c.email_count || 0} • 📱 {c.whatsapp_count || 0}</div>
                    </div>
                  ))}
                </div>
              )}
              <a
                href="/agenda"
                className="mt-3 inline-flex text-sm text-slate-700 hover:underline"
              >
                Voir le calendrier →
              </a>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-4 pt-4 border-t border-slate-200">
              <span className="text-sm text-slate-500">
                {pagination.total} campagne(s) au total
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 border border-slate-200 rounded text-sm disabled:opacity-50"
                >
                  Précédent
                </button>
                <span className="px-3 py-1 text-sm text-slate-600">
                  {pagination.page} / {totalPages}
                </span>
                <button
                  onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                  disabled={pagination.page >= totalPages}
                  className="px-3 py-1 border border-slate-200 rounded text-sm disabled:opacity-50"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
